import React, { useEffect, useState } from 'react';
import { Box, Button, Typography } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import { SourceFamilyTask } from '../api/workItems';
import { formatHumanDrawerDate } from '../utils/deriveNextStep';
import {
    FAMILY_STATUS_SHAPE,
    FamilyStepStatus,
    familyProgressSentence,
    familyStatusCounts,
    familyStepAccessibleName,
    familyStepStatus,
    familyStepWord,
    nextFamilyClockMs,
} from '../utils/sourceFamilyStatus';

/**
 * Fill colour per state. `new` is hollow: nothing has happened to it yet.
 * Locked by the source-home progress line (#305). Chrome hierarchy must not
 * recolor these segments; Mark complete updates a segment by changing the step's state.
 */
const STATUS_FILL: Record<Exclude<FamilyStepStatus, 'new'>, string> = {
    done: 'var(--color-status-done)',
    active: 'var(--color-status-active)',
    snoozed: 'var(--color-status-triage)',
};

const statusDotSx = (status: FamilyStepStatus) => {
    const base = {
        display: 'inline-block',
        width: 8,
        height: 8,
        flex: 'none',
    };
    if (status === 'new') {
        return { ...base, borderRadius: '50%', border: '1px solid var(--color-border-hover)', bgcolor: 'transparent' };
    }
    if (status === 'snoozed') {
        return { ...base, width: 7, height: 7, borderRadius: 0, transform: 'rotate(45deg)', bgcolor: STATUS_FILL.snoozed };
    }
    if (status === 'done') {
        return { ...base, borderRadius: '1px', bgcolor: STATUS_FILL.done };
    }
    return { ...base, borderRadius: '50%', bgcolor: STATUS_FILL.active };
};

const StatusMark: React.FC<{ status: FamilyStepStatus }> = ({ status }) => (
    <Box
        component="span"
        aria-hidden="true"
        data-status={status}
        data-shape={FAMILY_STATUS_SHAPE[status]}
        sx={statusDotSx(status)}
    />
);

const MAX_TIMEOUT_MS = 2_147_483_647;

function useFamilyClock(tasks: SourceFamilyTask[]): Date {
    const [now, setNow] = useState(() => new Date());
    const clockKey = tasks.map((task) => `${task.state || ''}|${task.snoozed_until || ''}`).join('|');
    useEffect(() => {
        const at = nextFamilyClockMs(tasks, now);
        if (at == null) return undefined;
        const delay = Math.min(Math.max(at - Date.now(), 0), MAX_TIMEOUT_MS);
        const id = window.setTimeout(() => setNow(new Date()), delay);
        return () => window.clearTimeout(id);
    }, [now, clockKey, tasks]);
    return now;
}

const focusRingSx = {
    '&:focus-visible': {
        outline: '2px solid var(--color-accent)',
        outlineOffset: '2px',
    },
};

interface SourceFamilyStripProps {
    tasks: SourceFamilyTask[];
    activeTaskId: string | null;
    onOpenSource: () => void;
    onOpenTask: (taskId: string) => void;
    /** Quieter than the step title. Omitted when the source has no name. */
    homeEyebrow?: string | null;
    homeTitle?: string | null;
}

/**
 * Shown while one next step is open. A text link back to the source, then the
 * other steps as quiet jumps. Not a numbered strip and not list position.
 */
const SourceFamilyStrip: React.FC<SourceFamilyStripProps> = ({
    tasks,
    activeTaskId,
    onOpenSource,
    onOpenTask,
    homeEyebrow = null,
    homeTitle = null,
}) => {
    const now = useFamilyClock(tasks);
    return (
        <Box
            role="group"
            aria-label="Next steps from this source"
            data-testid="WB_SOURCE_FAMILY"
            sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                gap: 0.75,
                px: 2,
                py: 1,
                borderBottom: '1px solid var(--color-border)',
                bgcolor: 'var(--color-bg-secondary)',
            }}
        >
            <Button
                type="button"
                size="small"
                data-testid="WB_SOURCE_FAMILY_BACK"
                onClick={onOpenSource}
                startIcon={<FontAwesomeIcon icon={faArrowLeft} style={{ fontSize: '0.75rem' }} />}
                sx={{
                    textTransform: 'none',
                    fontWeight: 650,
                    fontSize: '0.875rem',
                    color: 'var(--color-accent-text)',
                    minHeight: 36,
                    px: 0.5,
                    '&:hover': { bgcolor: 'var(--color-accent-muted)' },
                    ...focusRingSx,
                }}
            >
                Back to source
            </Button>
            {homeTitle && (
                <Box
                    data-testid="WB_DRAWER_SOURCE_HOME_STRIP"
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        width: '100%',
                        border: '1px solid var(--color-border)',
                        bgcolor: 'var(--color-bg-card)',
                        borderRadius: 2,
                        px: 1.25,
                        py: 0.75,
                    }}
                >
                    <Box sx={{ minWidth: 0 }}>
                        <Typography
                            component="p"
                            sx={{
                                m: 0,
                                fontSize: '0.625rem',
                                fontWeight: 700,
                                letterSpacing: '0.06em',
                                textTransform: 'uppercase',
                                color: 'var(--color-text-muted)',
                            }}
                        >
                            {homeEyebrow || 'From this source'}
                        </Typography>
                        <Typography
                            component="p"
                            sx={{
                                m: 0,
                                mt: 0.25,
                                fontSize: '0.8125rem',
                                fontWeight: 600,
                                color: 'var(--color-text-secondary)',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                            }}
                        >
                            {homeTitle}
                        </Typography>
                    </Box>
                </Box>
            )}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, alignItems: 'center', maxWidth: '100%' }}>
                {tasks.map((task) => {
                    const active = task.id === activeTaskId;
                    const status = familyStepStatus(task, now);
                    const word = familyStepWord(task, now);
                    return (
                        <Button
                            key={task.id}
                            type="button"
                            size="small"
                            data-testid="WB_SOURCE_FAMILY_STEP"
                            aria-current={active ? 'true' : undefined}
                            aria-label={familyStepAccessibleName(task, now)}
                            onClick={() => onOpenTask(task.id)}
                            sx={{
                                textTransform: 'none',
                                fontWeight: active ? 600 : 500,
                                fontSize: '0.75rem',
                                minHeight: 32,
                                px: 1.25,
                                gap: 0.75,
                                maxWidth: '100%',
                                borderRadius: 2,
                                border: `1px solid ${active ? 'var(--color-accent)' : 'var(--color-border)'}`,
                                color: active
                                    ? 'var(--color-accent-text)'
                                    : status === 'done'
                                        ? 'var(--color-text-muted)'
                                        : 'var(--color-text-secondary)',
                                bgcolor: active ? 'var(--color-accent-muted)' : 'transparent',
                                '&:hover': {
                                    bgcolor: active ? 'var(--color-accent-muted)' : 'var(--color-bg-card-hover)',
                                    borderColor: 'var(--color-accent)',
                                },
                                ...focusRingSx,
                            }}
                        >
                            <StatusMark status={status} />
                            <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>
                                {task.title}
                            </Box>
                            {word && (
                                <Box
                                    component="span"
                                    sx={{
                                        flex: 'none',
                                        fontWeight: 700,
                                        color: 'var(--color-text-secondary)',
                                    }}
                                >
                                    {word}
                                </Box>
                            )}
                        </Button>
                    );
                })}
            </Box>
        </Box>
    );
};

interface SourceFamilyProgressProps {
    tasks: SourceFamilyTask[];
}

/**
 * One thin line for the whole family: one segment per step, in list order,
 * coloured by that step's state. Under it, a count for each state that exists.
 * Segments are steps, not stages, so the line never implies an order of work.
 */
export const SourceFamilyProgress: React.FC<SourceFamilyProgressProps> = ({ tasks }) => {
    const now = useFamilyClock(tasks);
    if (tasks.length === 0) return null;
    const counts = familyStatusCounts(tasks, now);
    const sentence = familyProgressSentence(tasks, now);
    return (
        <Box sx={{ px: 2, pt: 1.5, pb: 0.5 }}>
            <Box
                role="img"
                aria-label={sentence}
                data-testid="WB_SOURCE_FAMILY_PROGRESS"
                sx={{
                    display: 'grid',
                    gridAutoFlow: 'column',
                    gridAutoColumns: '1fr',
                    gap: 0.5,
                    height: 5,
                }}
            >
                {tasks.map((task) => {
                    const status = familyStepStatus(task, now);
                    return (
                        <Box
                            key={task.id}
                            data-status={status}
                            sx={{
                                borderRadius: 3,
                                ...(status === 'new'
                                    ? { border: '1px solid var(--color-border-hover)', bgcolor: 'transparent' }
                                    : { bgcolor: STATUS_FILL[status] }),
                            }}
                        />
                    );
                })}
            </Box>
            <Box
                component="p"
                aria-hidden="true"
                data-testid="WB_SOURCE_FAMILY_PROGRESS_WORDS"
                sx={{
                    m: 0,
                    mt: 0.75,
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: '2px 12px',
                    fontSize: '0.75rem',
                    fontWeight: 700,
                    color: 'var(--color-text-secondary)',
                }}
            >
                {counts.map((entry) => (
                    <Box key={entry.status} component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
                        <StatusMark status={entry.status} />
                        {entry.label}
                    </Box>
                ))}
            </Box>
        </Box>
    );
};

interface SourceHomeStepsProps {
    tasks: SourceFamilyTask[];
    onOpenTask: (taskId: string) => void;
}

/**
 * Every next step from this source, on the source home. Each row carries one
 * word for its own state; a step nobody has touched stays plain. No complete
 * control here: finishing happens on the step.
 */
export const SourceHomeSteps: React.FC<SourceHomeStepsProps> = ({ tasks, onOpenTask }) => {
    const now = useFamilyClock(tasks);
    if (tasks.length === 0) return null;
    return (
        <Box sx={{ px: 2, pt: 0.5, pb: 1.5 }}>
            <Typography
                id="wb-source-home-steps-label"
                component="p"
                sx={{
                    m: 0,
                    mb: 0.75,
                    fontSize: '0.625rem',
                    fontWeight: 700,
                    letterSpacing: '0.08em',
                    textTransform: 'uppercase',
                    color: 'var(--color-text-muted)',
                }}
            >
                Next steps
            </Typography>
            <Box
                component="ul"
                data-testid="WB_SOURCE_HOME"
                aria-labelledby="wb-source-home-steps-label"
                sx={{
                    listStyle: 'none',
                    m: 0,
                    p: 0,
                    display: 'grid',
                    gap: 0.75,
                }}
            >
                {tasks.map((task) => {
                    const status = familyStepStatus(task, now);
                    const word = familyStepWord(task, now);
                    const finished = status === 'done';
                    const due = formatHumanDrawerDate(task.due_date);
                    return (
                        <Box component="li" key={task.id} sx={{ m: 0 }}>
                            <Button
                                type="button"
                                fullWidth
                                data-testid="WB_SOURCE_HOME_STEP"
                                onClick={() => onOpenTask(task.id)}
                                sx={{
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    gap: 1.5,
                                    textAlign: 'left',
                                    textTransform: 'none',
                                    fontWeight: finished ? 500 : 600,
                                    color: finished ? 'var(--color-text-muted)' : 'var(--color-text-primary)',
                                    bgcolor: 'var(--color-bg-card)',
                                    border: '1px solid var(--color-border)',
                                    borderRadius: 2,
                                    px: 1.5,
                                    py: 1,
                                    minHeight: 44,
                                    '&:hover': {
                                        bgcolor: 'var(--color-bg-card)',
                                        borderColor: 'var(--color-accent)',
                                    },
                                    ...focusRingSx,
                                }}
                            >
                                <Box component="span" sx={{ minWidth: 0, flex: '1 1 auto' }}>
                                    <Box component="span" sx={{ display: 'block' }}>{task.title}</Box>
                                    {due && (
                                        <Box
                                            component="span"
                                            sx={{ display: 'block', mt: 0.25, fontSize: '0.75rem', fontWeight: 500, color: 'var(--color-text-muted)' }}
                                        >
                                            {`Due ${due}`}
                                        </Box>
                                    )}
                                </Box>
                                {word && (
                                    <Typography
                                        component="span"
                                        data-testid="WB_SOURCE_HOME_STEP_STATE"
                                        sx={{
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            gap: 0.75,
                                            flex: 'none',
                                            fontSize: '0.75rem',
                                            fontWeight: 700,
                                            color: 'var(--color-text-secondary)',
                                        }}
                                    >
                                        <StatusMark status={status} />
                                        {word}
                                    </Typography>
                                )}
                            </Button>
                        </Box>
                    );
                })}
            </Box>
        </Box>
    );
};

export default SourceFamilyStrip;
